import {
  consumeImplicitJumpToken,
  socialHeadlessCall,
} from '$api/headless-api';
import { AuthRideError } from '$api/types/errors';
import { createTokenExchangeErrorOutcome } from '$flows/helpers/token-exchange-error';
import { PopupBlockedError } from '$scripts/events/dispatchers';
import { AppEvents } from '$scripts/events/events';
import { CriticalOutcome, Outcome } from '$scripts/outcome';
import { queryState as qs } from '$scripts/query-state';
import {
  socialFederatedPopup,
  socialWebDeeplinkPopup
} from '$scripts/urls/handlers';
import { socialWebDeeplinkUrl } from '$scripts/urls/list';
import { AUTH_FAILED_REASON } from '$shared/types/auth-failed-reasons';
import {
  GOOGLE_MOCK_PROVIDER,
  GOOGLE_SIGN_IN_CLIENT_ID,
  PROVIDERS,
  SocialProvider,
} from '$shared/types/social-provider';
import { getApplicationState } from '$store/application-store';
import {
  defaultHandleAuthCode,
  defaultHandleAuthFailed,
  defaultHandleToken,
} from '$traits/handlers';
import {
  GetOutcomes,
  RemainingOutcomes,
  outcomeFunction,
} from '$traits/helpers';
import {
  AuthCodeOutcome,
  AuthFailedOutcome,
  CompleteAccountRequiredOutcome,
  TokenOutcome
} from '$traits/outcomes';
import { convertError, envs } from '@sentry/shared';
import { ENVIRONMENTS } from '@sentry/shared/dist/environments';
import { getLoggers } from '$shared/loggers';
import { getAnalyticsState } from '$store/analytics-store';
import { createInteractionEvent } from '$scripts/analytics/events';

interface ActionDependencies {
  getApplicationState: typeof getApplicationState;
}

async function getFederatedToken(socialProvider: SocialProvider) {
  const { events, eventListener, queryState } = await getApplicationState();

  if (queryState.st_mock_fedtoken) {
    return 'mocked-token';
  }

  // Use the google-mock service for federated sign-in in non-prod environments
  const socialProviderForFederated =
    queryState.client_id === GOOGLE_SIGN_IN_CLIENT_ID &&
      envs.getAppEnv() !== ENVIRONMENTS.prod
      ? GOOGLE_MOCK_PROVIDER
      : socialProvider;

  const {
    data: { token },
  } = await socialFederatedPopup(socialProviderForFederated)(
    events,
    eventListener(),
    queryState
  );

  return token;
}

function createAction({ getApplicationState }: ActionDependencies) {
  return async function action(socialProvider: SocialProvider) {
    const { events, eventListener, queryState, config } =
      await getApplicationState();
    const { logInfo } = getLoggers();
    events.sendProvider(socialProvider);

    logInfo('Social sign-in action started');

    const shouldEnableCompleteAccount =
      'enableCompleteAccount' in config ? config.enableCompleteAccount : false;

    const shouldUseFederated =
      queryState.st_use_federated ||
      queryState.st_force_ab_test ||
      shouldEnableCompleteAccount ||
      sessionStorage.getItem('sentry_use_federated') === 'true';

    logInfo(`[AB-TEST] Should use federated: ${shouldUseFederated}`);
    const enableTest = shouldUseFederated && socialProvider === PROVIDERS.google;
    logInfo(`[AB-TEST] Part of test: ${enableTest}`);

    const { push } = getAnalyticsState();
    push(createInteractionEvent({ 
      eventName: `Account:IMS-Light:AB-Test:Activated:${enableTest}` 
    }));

    const sideA = enableTest && (Math.random() < 0.5 || queryState.st_force_ab_test === 'A');
    
    if (enableTest) {
      logInfo(`[AB-TEST] Test Side: ${sideA ? 'A' : 'B'}`);
      push(createInteractionEvent({ 
        eventName: `Account:IMS-Light:AB-Test:Side:${sideA ? 'A' : 'B'}` 
      }));
    }
    
    try {
      if (enableTest && sideA) {
        logInfo('[social-sign-in-with-popup] Starting federated flow - Google provider');
        const token = await getFederatedToken(socialProvider);
        logInfo('[social-sign-in-with-popup] Federated token received');
        // TODO: Call Social Headless to get IJT OR error (account creation req / others)
        // SUSI-20877: Integrate Headless Social API
        const {
          queryState: { client_id, scope },
        } = await getApplicationState();

        try {
          // for testing the user-exists flow, add the "locally_mocked_valid_user" response_type
          logInfo('[social-sign-in-with-popup] Social headless call');
          const r = await socialHeadlessCall(token, socialProvider, client_id);
          logInfo('[social-sign-in-with-popup] Social headless call completed');
          logInfo('[social-sign-in-with-popup] Implicit jump token call');
          const res = await consumeImplicitJumpToken(r.token, client_id);
          logInfo('[social-sign-in-with-popup] Implicit jump token consumed');

          if (res.access_token) {
            await logInfo('[social-sign-in-with-popup] Access token received from IJT');
            return new TokenOutcome({ token: res.access_token });
          }

          // if there's no access_token, that means the check failed
          await logInfo('[social-sign-in-with-popup] No access token from IJT, throwing error.');
          throw new Error();
        } catch (e: unknown) {
          logInfo('[social-sign-in-with-popup] Error during federated sign-in, attempting to handle');
          if (e instanceof Error) {
            const errorMessage = JSON.parse(e.message);
            if (
              errorMessage.error === 'ride_AdobeID_social' &&
              errorMessage.error_type === 'recoverable_user' &&
              // This is the description and parameter that needs to exist for this specific case.
              // If data is missing or description is different, then likely we are on a flow such as: social linking.
              // We need to handle it in this way to allow CSDK to use the same endpoint.
              errorMessage.error_description === 'Signup required' &&
              errorMessage.data != null
            ) {
              const fields = errorMessage.data;
              logInfo('[social-sign-in-with-popup] Complete account required -> CompleteAccountRequiredOutcome');
              return new CompleteAccountRequiredOutcome({
                fields,
                token,
                socialProvider,
                client_id,
              });
            } else {
              // This is handling the non-ride error case
              await logInfo('[social-sign-in-with-popup] Complete account not required -> Jumping to Tempest');
              return await createTokenExchangeErrorOutcome(
                client_id,
                queryState,
                token,
                socialProvider
              );
            }
          }

          console.error(e);
          await logInfo('[social-sign-in-with-popup] Unknown error in federated sign-in catch block');
        }

        // BRANCH 1 (from social headless): TODO: Implement complete account handling
        // SUSI-20877 -> SUSI-20962: Handle Complete Account outcome
        // return new CompleteAccountRequiredOutcome({ fields, token })

        // BRANCH 2 (from social headless): On failure of Social Headless API with ride error
        if (queryState.st_exchange_failed) {
          throw new AuthRideError();
        }

        // BRANCH 3 (from social headless): TODO: Implement token exchange for IJT, return correct token
        // SUSI-20876: Integrate with IJT Session API
        await logInfo('[social-sign-in-with-popup] Federated sign-in successful -> TokenOutcome');
        return new TokenOutcome({ token });

        // TODO: Implement error handling for ride errors
        // SUSI-20880: Redirect to Tempest for errors
        // return new AuthFailedOutcome({ reason: AUTH_FAILED_REASON.rideError, fallbackUrl })
      }

      logInfo('[social-sign-in-with-popup] Other cases not related to federated flow.');
      // Recovery for a withheld credential (VULN-36931 emit gate): if origin
      // validation fails at release time, the host receives on-auth-failed WITH
      // this fallback so it can redirect to the full-page social flow — matching
      // the catch below and the app-elm/react wrapper (popup.ts). Without it a
      // gate failure dead-ends the sign-in instead of degrading to a redirect.
      const fallbackUrl = socialWebDeeplinkUrl({
        ...qs.toObject(queryState),
        provider_id: socialProvider,
      })();
      if (queryState.response_type === 'code') {
        logInfo('[social-sign-in-with-popup] NOT FEDERATED -> AuthCodeOutcome');
        const {
          data: { code },
        } = await socialWebDeeplinkPopup(socialProvider).withAuthCode(
          events,
          eventListener(),
          queryState
        );
        return new AuthCodeOutcome({ code, fallbackUrl });
      } else {
        const {
          data: { access_token },
        } = await socialWebDeeplinkPopup(socialProvider).withAccessToken(
          events,
          eventListener(),
          queryState
        );
        await logInfo('[social-sign-in-with-popup] NOT FEDERATED -> TokenOutcome');
        return new TokenOutcome({ token: access_token, fallbackUrl });
      }

    } catch (e) {
      await logInfo('[social-sign-in-with-popup] Generic error in trait try/catch block');
      const fallbackUrl = socialWebDeeplinkUrl({
        ...qs.toObject(queryState),
        provider_id: socialProvider,
      })();

      if (e instanceof PopupBlockedError) {
        return new AuthFailedOutcome({
          reason: AUTH_FAILED_REASON.popupBlocked,
          fallbackUrl,
        });
      }

      if (e instanceof AuthRideError) {
        return new AuthFailedOutcome({
          reason: AUTH_FAILED_REASON.rideError,
          fallbackUrl,
        });
      }

      const error = convertError(e);
      return new CriticalOutcome(error);
    }
  };
}

export const outcomeAction = outcomeFunction(
  createAction({ getApplicationState })
);

export type TraitOutcome = GetOutcomes<typeof outcomeAction>;

export async function handleOutcome<T extends Outcome>(
  events: AppEvents,
  outcome: T
) {
  await defaultHandleToken(events, outcome);
  await defaultHandleAuthCode(events, outcome);
  defaultHandleAuthFailed(events, outcome);

  return outcome as RemainingOutcomes<T, TraitOutcome>;
}

export const trait = {
  action: outcomeAction,
  handle: handleOutcome,
};
